/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';
import { prefixValues } from '@/helpers/util';
import { AnalyticsEvent } from '@/services/analytics/models';
import { Product, Value, WebInteraction, AnalyticsDataLayerEvent, WebPageDetails } from '@/services/analytics/platforms/adobe-analytics/models';
import { AdobeCartAddEvent, AdobeCartViewEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';

export interface AdobeModuleState {
  isFromBag: boolean;
  isMakeItMyOwn: boolean;

  // The data layer may not be available 
  // when page loads, so we temporarily 
  // store any Adobe Analytics events here.
  adobeDataLayerEvents: AnalyticsEvent[];

  // Holds one AnalyticsEvent event to combine
  // to the next sent event.
  // New values override old values. Resets to empty object.
  oneAdobeDataLayerEvent: AnalyticsDataLayerEvent;

  // TRUE if oneAdobeDataLayerEvent is an empty object
  isOneEventEmpty: boolean;

  // When app loads, no properties have been 
  // sent until there is an AdobePageLoadEvent. 
  // We only want to send the initial properties 
  // when the first page load event that occurs
  haveInitialPropertiesBeenSent: boolean;

  // In order to reduce Adobe Data Layer 
  // server calls, when a 'linkClicked' event
  // triggers a subsequent 'pageloaded' event,
  // the 'linkClicked' event is stored until 
  // the subsequent 'pageloaded' event is fired
  linkClickedEvent: WebInteraction | null;

  // Add-to-bag will be stored until subsequest CartPageLoadEvent is fired.
  addToBagEvent: AdobeCartAddEvent | null;

  // Bag-view event to be stored until subsequent CartPageLoadEvent is fired.
  bagViewEvent: AdobeCartViewEvent | null;

  // Upsell impressions to be stored until CartPageLoadEvent is fired
  upsellImpression: Product[] | null;

  // Only show upsell impression if list has changed since last user login
  hasUpsellImpressionChanged: boolean;

  registrationCompleteEvent: Value | null;
  rewardsEnrollEvent: Value | null;

  // We only want to send certain pageLoaded 
  // events once per session
  hasHomePageBeenViewed: boolean;
  hasNutritionCalcPageBeenViewed: boolean;
  hasIngredientsPageBeenViewed: boolean;

  // Flag that detects when a meal is being edited for a one-tap reorder
  ifEditingOneTap: boolean;

  // Flag that detects when a customer has set a store for pick up
  ifCustomerSetLocation: boolean

  // Mapping of meals in bag to how the meals were added to the bag
  productAddMethodList: { mealId: string, method: string }[]

  // Current pageLoaded events are "fire and forget", meaning that AA currently has no context as to what page it's on
  // when firing a subsequent error in the flow. Until we overhaul the analytics implementation, we will track the
  // latest webPageDetails to have available when sending later adobe error events
  latestWebPageDetails: WebPageDetails | null;

  // Flag that detects if the CYOB modal was opened in the automatic pop-up flow.
  isCyobModalFromAutoPopup: boolean;
}

const modulePrefix = 'adobe/';
const MUTATION_NAMES = {
  SET_IS_FROM_BAG: 'setIsFromBag',
  SET_IS_MAKE_IT_MY_OWN: 'setIsMakeItMyOwn',
  SET_ADOBE_DATA_LAYER_EVENTS: 'setAdobeDataLayerEvents',
  SET_IS_ONE_EVENT_EMPTY: 'setIsOneEventEmpty',
  APPLY_ADOBE_DATA_LAYER_EVENT: 'applyAdobeDataLayerEvent',
  RESET_ADOBE_DATA_LAYER_EVENT: 'resetAdobeDataLayerEvent',
  SET_INITIAL_APP_LOAD_PROPERTIES_HAVE_BEEN_SENT: 'setInitialAppLoadProperitesHaveBeenSent',
  SET_LINK_CLICKED_EVENT: 'setLinkedClickEvent',
  SET_REGISTRATION_COMPLETE_EVENT: 'setRegistrationCompleteEvent',
  SET_ADD_TO_BAG_EVENT: 'setAddToBagEvent',
  SET_BAG_VIEW_EVENT: 'setBagViewEvent',
  SET_UPSELL_IMPRESSION: 'setUpsellImpression',
  SET_HAS_UPSELL_IMPRESSION_CHANGED: 'setHasUpsellImpressionChanged',
  SET_REWARDS_ENROLL_EVENT: 'setRewardsEnrollEvent',
  SET_HAS_HOME_PAGE_BEEN_VIEWED: 'setHasHomePageBeenViewd',
  SET_HAS_NUTRITION_CALC_PAGE_BEEN_VIEWED: 'setHasNutritionCalcPageBeenViewed',
  SET_HAS_INGREDIENTS_PAGE_BEEN_VIEWED: 'setHasIngredientsPageBeenViewed',
  SET_IF_EDITING_ONE_TAP: 'setIfEditingOneTap',
  SET_IF_CUSTOMER_SET_LOCATION: 'setIfCustomerSetLocation',
  SET_PRODUCT_ADD_METHOD: 'setProductAddMethod',
  CLEAR_PRODUCT_ADD_METHOD_LIST: 'clearProductAddMethodList',
  SET_LATEST_WEB_PAGE_DETAILS: 'setLatestWebPageDetails',
  SET_IS_CYOB_MODAL_FROM_AUTO_POPUP: 'setIsCyobModalFromAutoPopup'
};

const GETTER_NAMES = {
  ONE_ADOBE_DATA_LAYER_EVENT: 'oneAdobeDataLayerEvent',
  IS_ONE_EVENT_EMPTY: 'isOneEventEmpty'
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, modulePrefix);
export const GETTERS = prefixValues(GETTER_NAMES, modulePrefix);

export function makeAdobeModule(): Module<AdobeModuleState, AppState> {
  return {
    namespaced: true,
    state: {
      isFromBag: false,
      isMakeItMyOwn: false,
      isOneEventEmpty: true,
      adobeDataLayerEvents: [], 
      oneAdobeDataLayerEvent: {},
      haveInitialPropertiesBeenSent: false, 
      linkClickedEvent: null,
      addToBagEvent: null,
      bagViewEvent: null,
      upsellImpression: null,
      hasUpsellImpressionChanged: false,
      registrationCompleteEvent: null,
      rewardsEnrollEvent: null,
      hasHomePageBeenViewed: false,
      hasNutritionCalcPageBeenViewed: false,
      hasIngredientsPageBeenViewed: false,
      ifEditingOneTap: false,
      ifCustomerSetLocation: false,
      productAddMethodList: [],
      latestWebPageDetails: null,
      isCyobModalFromAutoPopup: false
    },
    mutations: {
      [MUTATION_NAMES.SET_IS_FROM_BAG]: (state, isFromBag: boolean) => {
        state.isFromBag = isFromBag;
      },
      [MUTATION_NAMES.SET_IS_MAKE_IT_MY_OWN]: (state, isMakeItMyOwn: boolean) => {
        state.isMakeItMyOwn = isMakeItMyOwn;
      },
      [MUTATION_NAMES.SET_ADOBE_DATA_LAYER_EVENTS]: (state, adobeDataLayerEvent: AnalyticsEvent) => {
        state.adobeDataLayerEvents.push(adobeDataLayerEvent);
      },
      [MUTATION_NAMES.APPLY_ADOBE_DATA_LAYER_EVENT]: (state, event: AnalyticsDataLayerEvent) => {
        state.oneAdobeDataLayerEvent = {... state.oneAdobeDataLayerEvent, ...event};
      },
      [MUTATION_NAMES.RESET_ADOBE_DATA_LAYER_EVENT]: (state) => {
        state.oneAdobeDataLayerEvent = {};
      },
      [MUTATION_NAMES.SET_IS_ONE_EVENT_EMPTY]: (state, isOneEventEmpty: boolean) => {
        state.isOneEventEmpty = isOneEventEmpty;
      },
      [MUTATION_NAMES.SET_INITIAL_APP_LOAD_PROPERTIES_HAVE_BEEN_SENT]: (state, initialPropertiesSent) => {
        state.haveInitialPropertiesBeenSent = initialPropertiesSent; 
      },
      [MUTATION_NAMES.SET_LINK_CLICKED_EVENT]: (state, webInteraction: WebInteraction | null) => {
        state.linkClickedEvent = webInteraction; 
      },
      [MUTATION_NAMES.SET_REGISTRATION_COMPLETE_EVENT]: (state, accountManagementAnalytics: Value | null) => {
        state.registrationCompleteEvent = accountManagementAnalytics; 
      },
      [MUTATION_NAMES.SET_REWARDS_ENROLL_EVENT]: (state, accountManagementAnalytics: Value | null) => {
        state.rewardsEnrollEvent = accountManagementAnalytics;
      },
      [MUTATION_NAMES.SET_HAS_HOME_PAGE_BEEN_VIEWED]: (state, hasHomePageBeenViewed: boolean) => {
        state.hasHomePageBeenViewed = hasHomePageBeenViewed;
      },
      [MUTATION_NAMES.SET_HAS_NUTRITION_CALC_PAGE_BEEN_VIEWED]: (state, hasNutritionCalcPageBeenViewed: boolean) => {
        state.hasNutritionCalcPageBeenViewed = hasNutritionCalcPageBeenViewed;
      },
      [MUTATION_NAMES.SET_HAS_INGREDIENTS_PAGE_BEEN_VIEWED]: (state, hasIngredientsPageBeenViewed: boolean) => {
        state.hasIngredientsPageBeenViewed = hasIngredientsPageBeenViewed;
      },
      [MUTATION_NAMES.SET_ADD_TO_BAG_EVENT]: (state, addToBagEvent: AdobeCartAddEvent) => {
        state.addToBagEvent = addToBagEvent;
      },
      [MUTATION_NAMES.SET_BAG_VIEW_EVENT]: (state, bagViewEvent: AdobeCartViewEvent) => {
        state.bagViewEvent = bagViewEvent;
      },
      [MUTATION_NAMES.SET_UPSELL_IMPRESSION]: (state, upsellImpression: Product[]) => {
        state.upsellImpression = upsellImpression;
      },
      [MUTATION_NAMES.SET_HAS_UPSELL_IMPRESSION_CHANGED]: (state, hasUpsellImpressionChanged: boolean) => {
        state.hasUpsellImpressionChanged = hasUpsellImpressionChanged;
      },
      [MUTATION_NAMES.SET_IF_EDITING_ONE_TAP]: (state, ifEditingOneTap: boolean) => {
        state.ifEditingOneTap = ifEditingOneTap;
      },
      [MUTATION_NAMES.SET_IF_CUSTOMER_SET_LOCATION]: (state, ifCustomerSetLocation: boolean) => {
        state.ifCustomerSetLocation = ifCustomerSetLocation
      },
      [MUTATION_NAMES.SET_PRODUCT_ADD_METHOD]: (state, productAddMethod: { mealId: string, method: string}) => {
        state.productAddMethodList.push(productAddMethod);
      },
      [MUTATION_NAMES.CLEAR_PRODUCT_ADD_METHOD_LIST]: (state) => {
        state.productAddMethodList = [];
      },
      [MUTATION_NAMES.SET_LATEST_WEB_PAGE_DETAILS]: (state, details: WebPageDetails) => {
        state.latestWebPageDetails = details;
      },
      [MUTATION_NAMES.SET_IS_CYOB_MODAL_FROM_AUTO_POPUP]: (state, isFromAutoPopup: boolean) => {
        state.isCyobModalFromAutoPopup = isFromAutoPopup;
      }
    },
    getters: {
      [GETTER_NAMES.ONE_ADOBE_DATA_LAYER_EVENT]: (state, getters, rootState) => {
        return rootState.adobe.oneAdobeDataLayerEvent;
      },
      [GETTER_NAMES.IS_ONE_EVENT_EMPTY]: (state, getters, rootState) => {
        return rootState.adobe.isOneEventEmpty;
      }
    }
  };
}
